/**
 * Canvas Drawing Utilities for Angle Visualization
 */

interface Point {
  x: number;
  y: number;
}

interface Keypoints {
  rightShoulder: Point;
  rightElbow: Point;
  rightWrist: Point;
  rightHip: Point;
  rightKnee: Point;
  rightAnkle: Point;
}

export interface AngleColorPair {
  arc: string;
  text: string;
}

export interface AngleRangeInfo {
  min: number;
  max: number;
}

export interface AngleOverride {
  color?: AngleColorPair;
  range?: AngleRangeInfo;
}

export interface AngleOverrides {
  knee?: AngleOverride;
  hip?: AngleOverride;
  trunk?: AngleOverride;
  elbow?: AngleOverride;
}

// Skeleton connections for right side
const SKELETON_CONNECTIONS = [
  ['rightShoulder', 'rightElbow'],
  ['rightElbow', 'rightWrist'],
  ['rightShoulder', 'rightHip'],
  ['rightHip', 'rightKnee'],
  ['rightKnee', 'rightAnkle']
];

/**
 * Draw skeleton lines on canvas
 */
export function drawSkeleton(
  ctx: CanvasRenderingContext2D,
  keypoints: Keypoints,
  color: string = 'rgba(16, 185, 129, 0.6)',
  lineWidth: number = 2.56
) {
  ctx.strokeStyle = color;
  ctx.lineWidth = lineWidth;
  
  SKELETON_CONNECTIONS.forEach(([joint1, joint2]) => {
    const kp1 = keypoints[joint1 as keyof Keypoints];
    const kp2 = keypoints[joint2 as keyof Keypoints];
    
    if (kp1 && kp2) {
      ctx.beginPath();
      ctx.moveTo(kp1.x, kp1.y);
      ctx.lineTo(kp2.x, kp2.y);
      ctx.stroke();
    }
  });
}

/**
 * Draw keypoint dots on canvas
 */
export function drawKeypoints(
  ctx: CanvasRenderingContext2D,
  keypoints: Keypoints,
  color: string = '#EF4444',
  radius: number = 3.415
) {
  ctx.fillStyle = color;
  
  Object.values(keypoints).forEach((kp: Point) => {
    if (kp) {
      ctx.beginPath();
      ctx.arc(kp.x, kp.y, radius, 0, 2 * Math.PI);
      ctx.fill();
    }
  });
}

// Per-angle-type colors
const ANGLE_COLORS = {
  knee:  { arc: 'rgba(255, 215, 0, 0.95)',   text: '#FFD700' },   // gold
  hip:   { arc: 'rgba(255, 120, 40, 0.95)',   text: '#FF7828' },   // orange
  trunk: { arc: 'rgba(100, 210, 255, 0.95)',  text: '#64D2FF' },   // cyan
  elbow: { arc: 'rgba(120, 255, 120, 0.95)',  text: '#78FF78' },   // lime
};

/**
 * Draw angle arc at a joint with a per-type color and labeled prefix.
 */
export function drawAngleArc(
  ctx: CanvasRenderingContext2D,
  vertex: Point,
  p1: Point,
  p3: Point,
  angle: number,
  radius: number = 40,
  color: { arc: string; text: string } = ANGLE_COLORS.knee,
  label: string = '',
  range?: AngleRangeInfo
) {
  const angle1 = Math.atan2(p1.y - vertex.y, p1.x - vertex.x);
  const angle2 = Math.atan2(p3.y - vertex.y, p3.x - vertex.x);

  let startAngle = angle1;
  let endAngle = angle2;
  let counterClockwise = false;

  let angleDiff = angle2 - angle1;
  if (angleDiff > Math.PI) {
    angleDiff -= 2 * Math.PI;
  } else if (angleDiff < -Math.PI) {
    angleDiff += 2 * Math.PI;
  }

  if (angleDiff < 0) {
    counterClockwise = true;
  }

  // Arc
  ctx.beginPath();
  ctx.arc(vertex.x, vertex.y, radius, startAngle, endAngle, counterClockwise);
  ctx.strokeStyle = color.arc;
  ctx.lineWidth = 3;
  ctx.stroke();

  // Label position at the midpoint of the arc
  const midAngle = startAngle + (counterClockwise ? -Math.abs(angleDiff) : Math.abs(angleDiff)) / 2;
  const tagX = vertex.x + Math.cos(midAngle) * (radius + 42);
  const tagY = vertex.y + Math.sin(midAngle) * (radius + 42);

  const labelText = label
    ? `${label} ${Math.round(angle)}\u00b0`
    : `${Math.round(angle)}\u00b0`;

  // Figma-style pill tag: color-coded dot + DM Mono text in a rounded pill
  ctx.save();
  ctx.font = '500 13px "DM Mono", monospace';
  const textWidth = ctx.measureText(labelText).width;

  const dotSize = 8;
  const gap = 7;
  const padX = 9;
  const padY = 5;
  const tagWidth = padX + dotSize + gap + textWidth + padX;
  const tagHeight = dotSize + padY * 2;
  const tagLeft = tagX - tagWidth / 2;
  const tagTop = tagY - tagHeight / 2;
  const cornerRadius = 6;

  function hexToRgba(hex: string, alpha: number): string {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const solidColor = color.text;
  const bgColor = hexToRgba(solidColor, 0.18);
  const borderColor = hexToRgba(solidColor, 0.55);

  // Dark backdrop for contrast over busy video frames
  const backdropPad = 3;
  ctx.beginPath();
  ctx.moveTo(tagLeft - backdropPad + cornerRadius, tagTop - backdropPad);
  ctx.lineTo(tagLeft + tagWidth + backdropPad - cornerRadius, tagTop - backdropPad);
  ctx.arcTo(tagLeft + tagWidth + backdropPad, tagTop - backdropPad, tagLeft + tagWidth + backdropPad, tagTop - backdropPad + cornerRadius, cornerRadius);
  ctx.lineTo(tagLeft + tagWidth + backdropPad, tagTop + tagHeight + backdropPad - cornerRadius);
  ctx.arcTo(tagLeft + tagWidth + backdropPad, tagTop + tagHeight + backdropPad, tagLeft + tagWidth + backdropPad - cornerRadius, tagTop + tagHeight + backdropPad, cornerRadius);
  ctx.lineTo(tagLeft - backdropPad + cornerRadius, tagTop + tagHeight + backdropPad);
  ctx.arcTo(tagLeft - backdropPad, tagTop + tagHeight + backdropPad, tagLeft - backdropPad, tagTop + tagHeight + backdropPad - cornerRadius, cornerRadius);
  ctx.lineTo(tagLeft - backdropPad, tagTop - backdropPad + cornerRadius);
  ctx.arcTo(tagLeft - backdropPad, tagTop - backdropPad, tagLeft - backdropPad + cornerRadius, tagTop - backdropPad, cornerRadius);
  ctx.closePath();
  ctx.fillStyle = 'rgba(10, 18, 30, 0.72)';
  ctx.fill();

  // Outer pill
  ctx.beginPath();
  ctx.moveTo(tagLeft + cornerRadius, tagTop);
  ctx.lineTo(tagLeft + tagWidth - cornerRadius, tagTop);
  ctx.arcTo(tagLeft + tagWidth, tagTop, tagLeft + tagWidth, tagTop + cornerRadius, cornerRadius);
  ctx.lineTo(tagLeft + tagWidth, tagTop + tagHeight - cornerRadius);
  ctx.arcTo(tagLeft + tagWidth, tagTop + tagHeight, tagLeft + tagWidth - cornerRadius, tagTop + tagHeight, cornerRadius);
  ctx.lineTo(tagLeft + cornerRadius, tagTop + tagHeight);
  ctx.arcTo(tagLeft, tagTop + tagHeight, tagLeft, tagTop + tagHeight - cornerRadius, cornerRadius);
  ctx.lineTo(tagLeft, tagTop + cornerRadius);
  ctx.arcTo(tagLeft, tagTop, tagLeft + cornerRadius, tagTop, cornerRadius);
  ctx.closePath();
  ctx.fillStyle = bgColor;
  ctx.fill();
  ctx.strokeStyle = borderColor;
  ctx.lineWidth = 1;
  ctx.stroke();

  // Inner color dot
  const dotX = tagLeft + padX + dotSize / 2;
  const dotY = tagTop + tagHeight / 2;
  ctx.beginPath();
  ctx.arc(dotX, dotY, dotSize / 2, 0, 2 * Math.PI);
  ctx.fillStyle = solidColor;
  ctx.fill();

  // Label text
  ctx.fillStyle = '#FFFFFF';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(labelText, tagLeft + padX + dotSize + gap, tagY);
  ctx.restore();
}

/**
 * Draw trunk angle arc at the hip joint relative to horizontal.
 * Uses a larger radius than the hip angle arc to avoid overlap.
 * A dashed horizontal reference line is drawn from the hip for clarity.
 */
function drawTrunkAngleArc(
  ctx: CanvasRenderingContext2D,
  hip: Point,
  shoulder: Point,
  angle: number,
  radius: number = 85,
  override?: AngleOverride
) {
  const color = override?.color ?? ANGLE_COLORS.trunk;

  // Horizontal reference point to the right of the hip
  const refPoint: Point = { x: hip.x + radius + 10, y: hip.y };

  // Dashed horizontal reference line
  ctx.save();
  ctx.setLineDash([6, 4]);
  ctx.strokeStyle = color.arc.replace('0.95)', '0.5)');
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.moveTo(hip.x, hip.y);
  ctx.lineTo(refPoint.x, hip.y);
  ctx.stroke();
  ctx.restore();

  drawAngleArc(ctx, hip, refPoint, shoulder, angle, radius, color, 'Back', override?.range);
}

export interface DrawOptions {
  /**
   * Draw the image, skeleton and keypoint dots but no angle arcs or labels.
   * The results page positions its own HTML markers over the canvas; without
   * this every measurement would render twice.
   */
  skeletonOnly?: boolean;
}

/**
 * Draw complete frame with skeleton and angles.
 * Pass `overrides` to apply status-based colors and range labels to individual angles.
 */
export function drawFrameWithAngles(
  ctx: CanvasRenderingContext2D,
  imageUrl: string,
  keypoints: Keypoints,
  kneeAngle: number,
  hipAngle: number,
  trunkAngle: number,
  elbowAngle: number,
  onComplete?: () => void,
  overrides?: AngleOverrides,
  options?: DrawOptions
) {
  const img = new Image();
  img.onload = () => {
    const canvas = ctx.canvas;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw image
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    // Draw skeleton
    drawSkeleton(ctx, keypoints);

    // Draw keypoints
    drawKeypoints(ctx, keypoints);

    if (options?.skeletonOnly) {
      onComplete?.();
      return;
    }

    // Draw knee angle arc (hip-knee-ankle)
    if (keypoints.rightHip && keypoints.rightKnee && keypoints.rightAnkle) {
      const kneeOverride = overrides?.knee;
      drawAngleArc(
        ctx,
        keypoints.rightKnee,
        keypoints.rightHip,
        keypoints.rightAnkle,
        kneeAngle,
        50,
        kneeOverride?.color ?? ANGLE_COLORS.knee,
        'Knee',
        kneeOverride?.range
      );
    }

    // Draw hip angle arc (shoulder-hip-knee) — smaller radius to sit inside trunk arc
    if (keypoints.rightShoulder && keypoints.rightHip && keypoints.rightKnee) {
      const hipOverride = overrides?.hip;
      drawAngleArc(
        ctx,
        keypoints.rightHip,
        keypoints.rightShoulder,
        keypoints.rightKnee,
        hipAngle,
        50,
        hipOverride?.color ?? ANGLE_COLORS.hip,
        'Hip',
        hipOverride?.range
      );
    }

    // Draw trunk angle arc (hip→shoulder vs. horizontal) — large radius (85) to avoid overlap
    if (keypoints.rightHip && keypoints.rightShoulder) {
      drawTrunkAngleArc(ctx, keypoints.rightHip, keypoints.rightShoulder, trunkAngle, 85, overrides?.trunk);
    }

    // Draw elbow angle arc (shoulder-elbow-wrist)
    if (keypoints.rightShoulder && keypoints.rightElbow && keypoints.rightWrist) {
      const elbowOverride = overrides?.elbow;
      drawAngleArc(
        ctx,
        keypoints.rightElbow,
        keypoints.rightShoulder,
        keypoints.rightWrist,
        elbowAngle,
        40,
        elbowOverride?.color ?? ANGLE_COLORS.elbow,
        'Elbow',
        elbowOverride?.range
      );
    }
    
    if (onComplete) {
      onComplete();
    }
  };
  
  img.onerror = () => {
    console.error('Failed to load image for angle visualization');
    if (onComplete) {
      onComplete();
    }
  };
  
  img.src = imageUrl;
}
